import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  FormControlLabel,
  FormGroup,
  Icon,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  TextField,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../../../elements';
import type { Theme } from '@mui/material/styles';
import { V4_BUTTON_DEFAULT, V4_DIALOG_CONTENT, V4_ICON_BUTTON, V4_OUTLINED_DEFAULT } from '../../../../theme/v4Compat';
import { useTranslation } from 'react-i18next';
import { ClearFilters, Loader, MedicalSupplyTypeSelect, Title } from '../../../../elements';
import { MedicalSupplyBottleViewModel } from '../../../../models/injection';
import { extractApiErrorMessage, notifications } from '../../../../utils';
import { MedicalSuppliesService } from '../../../../hooks';
import { DEFAULT_ROWS_PAGE_OPTIONS, LoaderSize } from '../../../../constants/constants';
import UserRole from '../../../../constants/user-role';
import useAuthContext from '../../../../hooks/useAuthContext';
import CreateMedicalSupplyBottleDialog from './components/CreateMedicalSupplyBottleDialog';
import { AnimalType } from '../../../../models/animal';
import { useFarmDateTime } from '../../../../hooks/useFarmDateTime';

interface IState {
  isBusy: boolean;
  loaded: boolean;
  reqInProgress: { [key: string]: boolean | undefined };
}

interface IPagination {
  page: number;
  rowsPerPage: number;
  total: number;
}

interface IFilters {
  number: string;
  lotNumber: string;
  animalType: number;
  showHidden: boolean;
}

const MedicalBottles: React.FC<any> = () => {
  // Variables__________________________________________________________________________
  const medicalSuppliesService = new MedicalSuppliesService();
  const { state } = useAuthContext();
  const { t } = useTranslation();
  const styles = sxStyles(useTheme());
  const { formatDate } = useFarmDateTime();
  const canManageBottles =
    state.role === UserRole.admin || (state.role === UserRole.veterinary && (state.businessUnits?.length ?? 0) > 0);
  const [tableState, setTableState] = React.useState<IState>({
    isBusy: false,
    loaded: false,
    reqInProgress: {}
  });
  const [filters, setFilters] = React.useState<IFilters>({
    number: '',
    lotNumber: '',
    animalType: 0,
    showHidden: false
  });
  const [pagination, setPagination] = React.useState<IPagination>({
    page: 0,
    rowsPerPage: 10,
    total: 0
  });
  const [bottles, setBottles] = useState<MedicalSupplyBottleViewModel[]>([]);
  const [total, setTotal] = useState<number>();
  const [isCreateDialogOpened, setIsCreateDialogOpened] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const [selBottle, setSelBottle] = React.useState<MedicalSupplyBottleViewModel>();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = React.useState(false);

  // Functions__________________________________________________________________________
  React.useEffect(() => {
    if (!tableState.loaded) {
      const loadData = async () => loadBottles();
      loadData().then();
    }
  }, [tableState.loaded]);

  const loadBottles = async () => {
    setTableState({ ...tableState, isBusy: true, loaded: false });

    const searchPage = pagination.page === 0 ? 1 : pagination.page + 1;

    medicalSuppliesService
      .getMedicalSupplyBottles(
        searchPage,
        pagination.rowsPerPage,
        filters.number,
        filters.lotNumber,
        filters.animalType,
        filters.showHidden
      )
      .then((result) => {
        setTotal(result.total);
        setBottles(result.data);
        setPagination({
          ...pagination,
          total: result.total,
          page: result.total <= result.size ? 0 : pagination.page
        });
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setTableState({ ...tableState, isBusy: false, loaded: true });
      });
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPagination({ ...pagination, page: newPage });
    setTableState({ ...tableState, loaded: false });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination({
      ...pagination,
      rowsPerPage: parseInt(event.target.value, 10)
    });
    setTableState({ ...tableState, loaded: false });
  };

  const clearFilters = () =>
    setFilters({
      lotNumber: '',
      number: '',
      animalType: 0,
      showHidden: false
    });

  const search = () => {
    setPagination({ ...pagination, page: 0 });
    setTableState({ ...tableState, loaded: false });
  };

  const addOrRemoveReqProg = (id: string, action: 'add' | 'remove') => {
    const newVal = { ...tableState };
    action === 'remove' ? (newVal.reqInProgress[id] = undefined) : (newVal.reqInProgress[id] = true);
    setTableState(newVal);
  };

  const handleHideBtn = (bottle: MedicalSupplyBottleViewModel) => {
    setIsMenuOpen(false);
    addOrRemoveReqProg(bottle.id, 'add');
    medicalSuppliesService
      .setBottleIsHiddenValue(bottle.id, !bottle.isHidden)
      .then(() => {
        const index = bottles.findIndex((b) => b.id === bottle.id);
        bottles[index].isHidden = !bottle.isHidden;
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        addOrRemoveReqProg(bottle.id, 'remove');
      });
  };

  const handleMenuClick = (bottle: MedicalSupplyBottleViewModel) => (event: React.MouseEvent<HTMLButtonElement>) => {
    setSelBottle(bottle);
    setAnchorEl(event.currentTarget);
    setIsMenuOpen(true);
  };

  const handleCopyClick = () => {
    setIsMenuOpen(false);
    setIsEditMode(false);
    setIsCreateDialogOpened(true);
  };

  const handleEditClick = () => {
    setIsMenuOpen(false);
    setIsEditMode(true);
    setIsCreateDialogOpened(true);
  };

  const handleRemoveClick = () => {
    setIsMenuOpen(false);
    setRemoveConfirmOpen(true);
  };

  const handleRemoveConfirm = () => {
    if (!selBottle) return;
    setRemoveConfirmOpen(false);
    addOrRemoveReqProg(selBottle.id, 'add');
    medicalSuppliesService
      .removeMedicalSupplyBottle(selBottle.id)
      .then(() => {
        notifications.success(t('f-bottle-remove-ok'));
        setTableState({ ...tableState, loaded: false });
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        addOrRemoveReqProg(selBottle.id, 'remove');
      });
  };

  const formatLastUsed = (lastUsedAt?: string | null): string => {
    if (!lastUsedAt) return '-';
    return formatDate(lastUsedAt);
  };

  const removeConfirmText = selBottle?.lastUsedAt
    ? t('f-bottle-remove-confirm', { date: formatDate(selBottle.lastUsedAt) })
    : t('f-bottle-remove-confirm-never');

  const renderBottleRow = (bottle: MedicalSupplyBottleViewModel) => (
    <TableRow key={bottle.id}>
      <TableCell padding="checkbox">
        {bottle.isHidden && (
          <Icon color="disabled" style={{ marginLeft: 16 }}>
            visibility_off
          </Icon>
        )}
      </TableCell>
      <TableCell>{bottle.medicalSupply.number}</TableCell>
      <TableCell>{bottle.medicalSupply.producer}</TableCell>
      <TableCell>{bottle.lotNumber}</TableCell>
      <TableCell>{bottle.treatmentNumber}</TableCell>
      <TableCell>{t(AnimalType[bottle.animalType])}</TableCell>
      <TableCell>{bottle.size}</TableCell>
      <TableCell>{formatLastUsed(bottle.lastUsedAt)}</TableCell>
      <TableCell padding="checkbox">
        <IconButton
          onClick={handleMenuClick(bottle)}
          disabled={tableState.reqInProgress[bottle.id]}
          sx={V4_ICON_BUTTON}
        >
          <Icon fontSize="large" color="primary">
            more_vert
          </Icon>
        </IconButton>
      </TableCell>
    </TableRow>
  );

  return (
    <Grid>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Grid container spacing={1}>
          <Icon sx={styles.headerIcon} fontSize={'large'}>
            medication
          </Icon>
          <Box sx={{ ml: 1, mt: 0.5 }}>
            <Title>{t('f-bottles')}</Title>
          </Box>
          <Grid item>
            <Chip label={total} variant="outlined" />
          </Grid>
        </Grid>

        <Box>
          <IconButton
            color="primary"
            onClick={() => setTableState({ ...tableState, loaded: false })}
            sx={V4_ICON_BUTTON}
          >
            <Icon fontSize="medium">{'refresh'}</Icon>
          </IconButton>

          {(state.role === UserRole.admin || state.role === UserRole.manager || state.role === UserRole.veterinary) && (
            <IconButton
              color="primary"
              aria-label={t('f-com-create-new')}
              onClick={() => {
                setSelBottle(undefined);
                setIsCreateDialogOpened(true);
              }}
              sx={V4_ICON_BUTTON}
            >
              <Icon fontSize="medium">{'add_circle'}</Icon>
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Grid container item spacing={1} alignItems={'center'} sx={styles.mt2}>
        <Grid item md={3} sm={6} xs={12}>
          <form
            noValidate
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              setTableState({ ...tableState, loaded: false });
            }}
          >
            <TextField
              label={t('f-supp-name')}
              variant="outlined"
              size="small"
              slotProps={{
                input: {
                  endAdornment: filters.number && (
                    <InputAdornment position="end">
                      <IconButton edge="end" onClick={() => setFilters({ ...filters, number: '' })} sx={V4_ICON_BUTTON}>
                        <Icon fontSize="small">clear</Icon>
                      </IconButton>
                    </InputAdornment>
                  )
                }
              }}
              fullWidth
              value={filters.number}
              onChange={(e) => setFilters({ ...filters, number: e.target.value })}
            />
          </form>
        </Grid>

        <Grid item md={3} sm={6} xs={12}>
          <form
            noValidate
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              setTableState({ ...tableState, loaded: false });
            }}
          >
            <TextField
              label={t('f-com-lot')}
              variant="outlined"
              fullWidth
              size="small"
              slotProps={{
                input: {
                  endAdornment: filters.lotNumber && (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setFilters({ ...filters, lotNumber: '' })}
                        sx={V4_ICON_BUTTON}
                      >
                        <Icon fontSize="small">clear</Icon>
                      </IconButton>
                    </InputAdornment>
                  )
                }
              }}
              value={filters.lotNumber}
              onChange={(e) => setFilters({ ...filters, lotNumber: e.target.value })}
            />
          </form>
        </Grid>

        <Grid item md={3} sm={6} xs={12}>
          <MedicalSupplyTypeSelect
            allowAny
            fullWidth
            value={filters.animalType}
            onSupplyTypeChange={(value) => setFilters({ ...filters, animalType: value })}
          />
        </Grid>

        <Grid item md={3} sm={6} xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ClearFilters onClick={clearFilters} />

            <Button
              id="searchBtn"
              variant="contained"
              color="inherit"
              fullWidth
              onClick={search}
              startIcon={<Icon>{'search'}</Icon>}
              sx={V4_BUTTON_DEFAULT}
            >
              {t('f-search')}
            </Button>
          </Box>
        </Grid>

        <Grid item md={3} sm={6} xs={12}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showHidden}
                  onChange={(_event, checked) => {
                    setFilters({ ...filters, showHidden: checked });
                    setTableState({ ...tableState, loaded: false });
                  }}
                  color="secondary"
                />
              }
              label={t('f-show-hidden')}
            />
          </FormGroup>
        </Grid>
      </Grid>

      <TableContainer component={Paper} elevation={1} sx={styles.mt2}>
        <Table>
          <TableBody>
            {tableState.loaded ? (
              bottles && bottles.length > 0 ? (
                <TableRow>
                  <TableCell variant="head"></TableCell>
                  <TableCell variant="head">{t('f-supply')}</TableCell>
                  <TableCell variant="head">{t('f-supplies-producer')}</TableCell>
                  <TableCell variant="head">{t('m-global-lot-number')}</TableCell>
                  <TableCell variant="head">{t('f-com-treat')}</TableCell>
                  <TableCell variant="head">{t('m-animal-type')}</TableCell>
                  <TableCell variant="head">{t('f-size')}</TableCell>
                  <TableCell variant="head">{t('f-last-used')}</TableCell>
                  <TableCell variant="head"></TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell variant="head" sx={styles.noDataBox}>
                    {t('f-no-data')}
                  </TableCell>
                </TableRow>
              )
            ) : (
              <TableRow sx={styles.spinnerBox}>
                <TableCell>
                  <Loader size={LoaderSize.MEDIUM} isLoading />
                </TableCell>
              </TableRow>
            )}

            {bottles && bottles.length > 0 && tableState.loaded
              ? bottles.map((bottle) => renderBottleRow(bottle))
              : null}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                count={pagination.total}
                rowsPerPage={pagination.rowsPerPage}
                page={pagination.page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <CreateMedicalSupplyBottleDialog
        opened={isCreateDialogOpened}
        selBottle={selBottle}
        isEditMode={isEditMode}
        onSuccess={() => setTableState({ ...tableState, loaded: false })}
        setDialog={(open: boolean) => {
          if (!open) setIsEditMode(false);
          setIsCreateDialogOpened(open);
        }}
      />
      <Menu
        id="fade-menu"
        anchorEl={anchorEl}
        keepMounted
        open={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        slots={{ transition: Fade }}
      >
        {canManageBottles && (
          <MenuItem onClick={handleEditClick}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Icon fontSize="small">edit</Icon>
              <Box sx={{ ml: 1, mr: 2 }}>{t('f-com-edit')}</Box>
            </Box>
          </MenuItem>
        )}
        <MenuItem onClick={handleCopyClick}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Icon fontSize="small">content_copy</Icon>
            <Box sx={{ ml: 1, mr: 2 }}>{t('f-copy')}</Box>
          </Box>
        </MenuItem>
        <MenuItem onClick={() => selBottle && handleHideBtn(selBottle)}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Icon fontSize="small">{selBottle?.isHidden ? 'visibility' : 'visibility_off'}</Icon>
            <Box sx={{ ml: 1, mr: 2 }}>{t(selBottle?.isHidden ? 'f-show' : 'f-hide')}</Box>
          </Box>
        </MenuItem>
        {canManageBottles && (
          <MenuItem onClick={handleRemoveClick}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Icon fontSize="small" color="error">
                delete
              </Icon>
              <Box style={{ color: '#f44336' }} sx={{ ml: 1, mr: 2 }}>
                {t('f-com-remove')}
              </Box>
            </Box>
          </MenuItem>
        )}
      </Menu>

      <Dialog open={removeConfirmOpen} onClose={() => setRemoveConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('f-com-remove')}</DialogTitle>
        <DialogContent sx={V4_DIALOG_CONTENT}>
          <DialogContentText>{removeConfirmText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRemoveConfirmOpen(false)}
            variant="outlined"
            color="inherit"
            sx={V4_OUTLINED_DEFAULT}
          >
            {t('f-com-cancel')}
          </Button>
          <Button onClick={handleRemoveConfirm} variant="contained" color="secondary" startIcon={<Icon>delete</Icon>}>
            {t('f-com-remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default MedicalBottles;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column',
      minHeight: 200
    },
    spinnerBox: {
      marginTop: '32px',
      marginBottom: '32px'
    },
    noDataBox: {
      minHeight: 75,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'gray'
    },
    formControl: {
      margin: theme.spacing(1)
    },
    headerIcon: {
      color: theme.palette.primary.main
    },
    header: {
      paddingTop: '4px'
    },
    ml1: {
      marginLeft: theme.spacing(1)
    },
    h40px: {
      height: 40
    },
    dFlex: {
      display: 'flex'
    },
    autoAlign: {
      alignItems: 'center',
      padding: theme.spacing(1),
      paddingLeft: 0,
      paddingRight: 0,
      [theme.breakpoints.up('sm')]: {
        justifyContent: 'flex-end'
      }
    },
    mt2: {
      marginTop: theme.spacing(2)
    }
  }) as const;
